import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MessageSquare, Send } from 'lucide-react';
import type { TaskComment } from '@/types/task';
import { tasksService } from '@/services/tasks-service';
import { useToast } from '@/hooks/use-toast';

interface TaskDetailCommentsProps {
  readonly taskId: string;
  readonly comments: TaskComment[];
  readonly onCommentAdded: (comment: TaskComment) => void;
  isEditMode?: boolean;
}

export function TaskDetailComments({
  taskId,
  comments,
  onCommentAdded,
  isEditMode = false,
}: Readonly<TaskDetailCommentsProps>) {
  const { toast } = useToast();
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddComment = async () => {
    if (!newComment.trim() || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      // Call the service to persist the comment
      const savedComment = await tasksService.addComment(taskId, newComment.trim());
      
      // Map the response to TaskComment format
      const comment: TaskComment = {
        id: savedComment.id,
        taskId,
        content: savedComment.comment || savedComment.content || newComment.trim(),
        createdAt: savedComment.createdDate || savedComment.createdAt || new Date().toISOString(),
        createdBy: savedComment.createdByUserId || savedComment.createdBy || 'user-1',
        createdByName: savedComment.createdByUserName || savedComment.createdByName || 'Current User',
        createdByAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100',
      };
      
      onCommentAdded(comment);
      setNewComment('');
      
      toast({
        title: 'Comment Added',
        description: 'Your comment has been posted.',
      });
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast({
        title: 'Error',
        description: 'Failed to add comment. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if comment is from current user
  const isCurrentUser = (comment: TaskComment) => {
    // For now, we'll check if it's "Current User" or the createdBy matches a known pattern
    // In a real app, you'd compare with the actual logged-in user ID
    return comment.createdByName === 'Current User' || comment.createdBy === 'user-1';
  };

  return (
    <div className={`grid grid-cols-1 gap-6 h-full ${isEditMode ? 'lg:grid-cols-3' : ''}`}>
      {/* Comments List - Left Side (2/3) */}
      <div className={`flex flex-col ${isEditMode ? 'lg:col-span-2' : ''}`}>
        <Card className="flex-1 flex flex-col">
          <CardContent className="p-4 flex-1 overflow-y-auto flex flex-col">
        {comments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
              <MessageSquare className="h-8 w-8 text-muted-foreground/50" />
            </div>
            <p className="text-sm font-medium text-foreground mb-1">No comments yet</p>
            <p className="text-xs text-muted-foreground">Be the first to share your thoughts</p>
          </div>
        ) : (
          <div className="space-y-3 flex-1">
            {comments.map((comment) => {
              const isOwn = isCurrentUser(comment);
              return (
                <div 
                  key={comment.id} 
                  className={`flex gap-3 ${isOwn ? 'flex-row-reverse' : ''}`}
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary">
                      {comment.createdByName?.[0] ?? 'S'}
                    </span>
                  </div>
                  <Card className={`flex-1 max-w-[80%] ${isOwn ? 'bg-primary/5 border-primary/20' : 'bg-background'}`}>
                    <CardContent className="p-3">
                      <div className={`flex items-baseline gap-2 mb-1.5 ${isOwn ? 'flex-row-reverse' : ''}`}>
                        <span className="font-medium text-sm">{comment.createdByName || 'System'}</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(comment.createdAt).toLocaleString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                          })}
                        </span>
                      </div>
                      <p className={`text-sm text-foreground/90 ${isOwn ? 'text-right' : ''}`}>{comment.content}</p>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        )}
          </CardContent>
        </Card>
      </div>

      {/* Add Comment Card - Right Side (1/3) */}
      {isEditMode && (
      <div className="lg:col-span-1">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-4 w-4" />
              Add Comment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-muted-foreground">
              Share your thoughts or updates
            </div>
            <Textarea
              placeholder="Add a comment... Use @ to mention someone"
              value={newComment}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewComment(e.target.value)}
              rows={6}
              className="resize-none"
            />
            <Button 
              onClick={handleAddComment} 
              disabled={!newComment.trim() || isSubmitting}
              className="w-full"
            >
              <Send className="h-4 w-4 mr-2" />
              {isSubmitting ? 'Posting...' : 'Post Comment'}
            </Button>
          </CardContent>
        </Card>
      </div>
      )}
    </div>
  );
}
